import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Layout4 from "../DataLayouts/Layout4";
import PermissionSelector, {
  getPermissionActionId,
} from "../../Components/Permissions/PermissionSelector";
import {
  getApplicationRolePermissions,
  listApplicationModuleActions,
  listApplicationModules,
  listApplicationRolePermissions,
  listApplicationRoles,
  saveApplicationRolePermissions,
} from "../../Apis/ApplicationAdminUserApis";

function normalizeActionsFromModules(modules) {
  const groups = [];
  (modules || []).forEach((mod) => {
    const moduleName =
      mod.module_name || mod.application_module_name || mod.name || "Module";
    if (Array.isArray(mod.features) && mod.features.length > 0) {
      mod.features.forEach((feature) => {
        const actions = feature.actions || feature.module_action || [];
        if (!actions.length) return;
        groups.push({
          label: `${moduleName} — ${
            feature.module_feature_name ||
            feature.feature_name ||
            "Feature"
          }`,
          actions,
        });
      });
      return;
    }
    const actions = mod.module_action || mod.ModuleAction || mod.actions || [];
    if (actions.length) {
      groups.push({ label: moduleName, actions });
    }
  });
  return groups;
}

function normalizeActionsFlat(actions) {
  const byCategory = {};
  (actions || []).forEach((action) => {
    const category = action.category || "Actions";
    if (!byCategory[category]) byCategory[category] = [];
    byCategory[category].push(action);
  });
  return Object.entries(byCategory).map(([label, list]) => ({
    label,
    actions: list,
  }));
}

export default function ApplicationRolePermissionForm({ mode = "add" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isView = mode === "view";
  const isEdit = mode === "edit";

  const [roles, setRoles] = useState([]);
  const [roleIdsConfigured, setRoleIdsConfigured] = useState(() => new Set());
  const [groups, setGroups] = useState([]);
  const [roleId, setRoleId] = useState(id ? String(id) : "");
  const [allowedIds, setAllowedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [roleList, perms, modules, flatActions] = await Promise.all([
          listApplicationRoles(),
          listApplicationRolePermissions(),
          listApplicationModules().catch(() => []),
          listApplicationModuleActions().catch(() => []),
        ]);
        if (cancelled) return;

        setRoles(roleList || []);

        const configured = new Set();
        (perms || []).forEach((p) => {
          const rid =
            p.application_user_role_id ??
            p.applicationUserRole?.application_user_role_id;
          if (rid) configured.add(Number(rid));
        });
        setRoleIdsConfigured(configured);

        let nextGroups = normalizeActionsFromModules(modules);
        if (!nextGroups.length) {
          nextGroups = normalizeActionsFlat(
            Array.isArray(flatActions)
              ? flatActions
              : flatActions?.moduleaction || []
          );
        }
        setGroups(nextGroups);

        if (id) {
          const detail = await getApplicationRolePermissions(id);
          const selected = new Set();
          (detail?.permissions || []).forEach((p) => {
            if (p.permission_allowed) {
              selected.add(Number(p.application_module_action_id));
            }
          });
          setAllowedIds(selected);
          setRoleId(String(detail?.application_user_role_id || id));
        }
      } catch (err) {
        toast.error(err.message || "Failed to load form data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const roleOptions = useMemo(() => {
    if (isEdit || isView) return roles;
    return roles.filter(
      (r) => !roleIdsConfigured.has(Number(r.application_user_role_id))
    );
  }, [roles, roleIdsConfigured, isEdit, isView]);

  const allActionIds = useMemo(() => {
    const ids = [];
    groups.forEach((g) => {
      (g.actions || []).forEach((a) => {
        const actionId = getPermissionActionId(a);
        if (actionId) ids.push(actionId);
      });
    });
    return ids;
  }, [groups]);

  const toggleAction = (actionId) => {
    if (isView || !actionId) return;
    setAllowedIds((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) next.delete(actionId);
      else next.add(actionId);
      return next;
    });
  };

  const toggleGroup = (actions, selectAll) => {
    if (isView) return;
    setAllowedIds((prev) => {
      const next = new Set(prev);
      (actions || []).forEach((action) => {
        const actionId = getPermissionActionId(action);
        if (!actionId) return;
        if (selectAll) next.add(actionId);
        else next.delete(actionId);
      });
      return next;
    });
  };

  const toggleAll = (selectAll) => {
    if (isView) return;
    setAllowedIds(selectAll ? new Set(allActionIds) : new Set());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) return;
    if (!roleId) {
      toast.error("Select a role.");
      return;
    }
    setSaving(true);
    try {
      const permissions = allActionIds.map((actionId) => ({
        application_module_action_id: actionId,
        permission_allowed: allowedIds.has(actionId),
      }));
      // Also include any selected ids not in catalog (edge case)
      allowedIds.forEach((actionId) => {
        if (!allActionIds.includes(actionId)) {
          permissions.push({
            application_module_action_id: actionId,
            permission_allowed: true,
          });
        }
      });

      await saveApplicationRolePermissions({
        application_user_role_id: Number(roleId),
        permissions,
      });
      toast.success("Role permissions saved");
      navigate("/application/user-role-permission");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const title = isView
    ? "View role permissions"
    : isEdit
      ? "Edit role permissions"
      : "Add role permissions";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900 }}>
      <Layout4
        heading={title}
        subtitle="Grant module actions to a role"
        showAddButton={false}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              select
              label="Role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              disabled={isView || isEdit}
              required
              fullWidth
            >
              {roleOptions.map((role) => (
                <MenuItem
                  key={role.application_user_role_id}
                  value={String(role.application_user_role_id)}
                >
                  {role.user_role_name || role.role_name}
                </MenuItem>
              ))}
            </TextField>

            {!isView && allActionIds.length > 0 && (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => toggleAll(true)}
                  sx={{ textTransform: "none" }}
                >
                  Select all permissions
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => toggleAll(false)}
                  disabled={allowedIds.size === 0}
                  sx={{ textTransform: "none" }}
                >
                  Clear all
                </Button>
              </Stack>
            )}

            <PermissionSelector
              groups={groups}
              allowedIds={allowedIds}
              onToggle={toggleAction}
              onToggleGroup={toggleGroup}
              disabled={isView}
              showSelectAll={!isView}
            />

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => navigate("/application/user-role-permission")}
              >
                Back
              </Button>
              {!isView && (
                <Button type="submit" variant="contained" disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
